/* eslint-disable no-case-declarations */
import { all, call, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import moment from 'moment';
import { get, omit } from 'lodash-es';
// eslint-disable-next-line no-unused-vars
import fr from 'moment/locale/fr';
import {
  getQuotation,
  getQuotationOfferV2,
  getQuotationsStore,
  getPrefUserInfo,
  getQuotationsStrings,
  getSystem,
} from './selectors';

import {
  fetchNewQuotationSuccess,
  fetchNewQuotationFail,
  fetchNewQuotationSRSuccess,
  fetchNewQuotationSRFail,
  fetchQuotationSuccess,
  fetchQuotationFail,
  fetchQuotationSRSuccess,
  fetchQuotationSRFail,
  putQuotationSuccess,
  putQuotationFail,
  putQuotationSR,
  putQuotationSRSuccess,
  putQuotationSRFail,
  fetchQuotationsSuccess,
  fetchQuotationsFail,
  fetchQuotationsSRSuccess,
  fetchQuotationsSRFail,
  fetchProductSuccess,
  fetchProductFail,
  fetchLendersSuccess,
  fetchLendersFail,
  fetchLenderAddressesSuccess,
  fetchLenderAddressesFail,
  fetchKindsSuccess,
  fetchKindsFail,
  fetchDeferralsSuccess,
  fetchDeferralsFail,
  fetchCalculateQuotationSuccess,
  fetchCalculateQuotationFail,
  fetchCalculateOnlyQuotationSR,
  fetchCalculateQuotationSRSuccess,
  fetchCalculateQuotationSRFail,
  fetchBanksSuccess,
  fetchBanksFail,
  submitQuotationSuccess,
  submitQuotationFail,
  cancelQuotationSuccess,
  cancelQuotationFail,
  cancelQuotationSRSuccess,
  cancelQuotationSRFail,
  putOnHoldQuotationSuccess,
  putOnHoldQuotationFail,
  printQuotationSuccess,
  printQuotationFail,
  printQuotationSRSuccess,
  printQuotationSRFail,
  validateIbanSuccess,
  validateIbanFail,
  duplicateQuotationSRSuccess,
  duplicateQuotationSRFail,
  submitQuotationsSuccess,
  submitQuotationsFail,
  submitOnlyQuotationsSR,
  submitQuotationsSRSuccess,
  submitQuotationsSRFail,
  validateQuotationSuccess,
  validateQuotationFail,
  validateQuotationSRSuccess,
  validateQuotationSRFail,
} from './actions';
import {
  FETCH_NEW_QUOTATION_REQUEST,
  FETCH_NEW_QUOTATION_SR_REQUEST,
  FETCH_QUOTATION_REQUEST,
  FETCH_QUOTATION_SR_REQUEST,
  PUT_QUOTATION_REQUEST,
  PUT_QUOTATION_SR_REQUEST,
  FETCH_QUOTATIONS_REQUEST,
  FETCH_QUOTATIONS_SR_REQUEST,
  FETCH_PRODUCT_REQUEST,
  FETCH_LENDERS_REQUEST,
  FETCH_LENDER_ADDRESSES_REQUEST,
  FETCH_KINDS_REQUEST,
  FETCH_DEFERRALS_REQUEST,
  FETCH_CALCULATE_REQUEST,
  FETCH_CALCULATE_SR_REQUEST,
  FETCH_CALCULATE_ONLY_SR_REQUEST,
  FETCH_BANKS_REQUEST,
  SUBMIT_QUOTATION_REQUEST,
  CANCEL_QUOTATION_REQUEST,
  CANCEL_QUOTATION_SR_REQUEST,
  PUTONHOLD_QUOTATION_REQUEST,
  PRINT_QUOTATION_REQUEST,
  PRINT_QUOTATION_SR_REQUEST,
  VALIDATE_IBAN_REQUEST,
  DUPLICATE_QUOTATION_SR_REQUEST,
  SUBMIT_QUOTATIONS_REQUEST,
  SUBMIT_QUOTATIONS_SR_REQUEST,
  SUBMIT_ONLY_QUOTATIONS_SR_REQUEST,
  VALIDATE_QUOTATION_REQUEST,
  VALIDATE_QUOTATION_SR_REQUEST,
} from './actionTypes';
import { logout } from '../Auth/actions';
import { getOptions, processResponse, sanitizeQuotation } from '../../Utils/Utils';
import { API, fetchFromApi } from '../apis';

import { getParamForCommercialProductsRequest } from '../Products/utils';
import { makeCompatibleWithAPIv1, getValidateFront, getAlerts } from './transform';
import { getSalesRouteFromQuotation } from './transformToSR';
import { getQuotationFromSalesRoute } from './transformToQuotation';
import { getOfferFromV2 } from './transformOfferToV1';

function* quotations() {
  const params = {
    // quote_status: status || undefined,
    _fields: encodeURI('id,quote_status,createdOn,commercial_product,transform,covered,subscriptions'),
  };
  const extraHeaders = {
    Range: '1-10000',
  };
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: API.quotation.V1__FETCH__ALL(), params, extraHeaders })
  );

  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(fetchQuotationsSuccess({ data }));
      break;
    case 'fail':
    case 'forbidden':
      yield put(fetchQuotationsFail({ error: alerts }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}

function* quotationsSR() {
  let params = getParamForCommercialProductsRequest(new Date(), get(yield select(getSystem), 'dateFormat'));

  const { response: responseCommProd, error: errorCommProd } = yield call(
    fetchFromApi,
    getOptions({ url: API.commercial_product.FETCH__ALL(), params })
  );
  if (responseCommProd) {
    params = {
      mask: encodeURI('b2b'),
      kind: encodeURI('quotation'),
    };
    const { response, error } = yield call(fetchFromApi, getOptions({ url: API.salesroute.FETCH__ALL(), params }));
    const { type, data, alerts } = processResponse({ response, error });
    switch (type) {
      case 'success':
        yield put(fetchQuotationsSRSuccess({ data: { commercialproducts: responseCommProd.data, quotations: data } }));
        break;
      case 'fail':
      case 'forbidden':
        yield put(fetchQuotationsSRFail({ error: alerts }));
        break;
      case 'logout':
        yield put(logout());
        break;
      default:
        break;
    }
  } else {
    const { type, alerts } = processResponse({ error: errorCommProd });

    switch (type) {
      case 'fail':
      case 'forbidden':
        yield put(fetchQuotationsSRFail({ error: alerts }));
        break;
      case 'logout':
        yield put(logout());
        break;
      default:
        break;
    }
  }
}

function* fetchProduct({ id }) {
  const { response, error } = yield call(fetchFromApi, getOptions({ url: API.product.FETCH__BY_ID(id) }));
  const { type, data, alerts } = processResponse({ response, error });

  switch (type) {
    case 'success':
      yield put(fetchProductSuccess(data, id));
      break;
    case 'fail':
    case 'forbidden':
      yield put(fetchProductFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}

function* getProductInfos(id, isDuplicated = false) {
  const { product, productCalc } = yield all({
    product: call(fetchFromApi, getOptions({ url: API.product.FETCH__BY_ID(id) })),
    productCalc: call(
      fetchFromApi,
      getOptions({ url: API.product.FETCH__CALC(), method: 'POST', data: { id, noDefault: isDuplicated } })
    ),
  });

  if (product.response && productCalc.response) {
    return {
      type: 'success',
      data: { product: product.response.data, productCalc: productCalc.response.data, productId: id },
    };
  }

  let alertsProduct = [];
  let typeProduct;

  if (!product.response) {
    const r = processResponse({ error: product.error });
    alertsProduct = r.alerts;
    typeProduct = r.type;
  }
  if (!productCalc.response) {
    const r = processResponse({ error: productCalc.error });
    alertsProduct = [...alertsProduct, ...r.alerts];

    if (typeProduct !== 'logout') typeProduct = r.type;
  }

  return { type: typeProduct, alerts: alertsProduct };
}

function* newquotation({ id, date, dateFormat, commercialproduct }) {
  const strDate = moment(date).isValid() ? moment(date).format(dateFormat) : moment(new Date()).format(dateFormat);
  const payload = {
    product: id,
    date: strDate,
    commercial_product: { id: commercialproduct.value, rec_name: commercialproduct.label },
  };

  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: API.quotation.V1__CREATE(), method: 'POST', data: payload })
  );

  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(fetchNewQuotationSuccess({ data }));
      break;
    case 'fail':
    case 'forbidden':
      yield put(fetchNewQuotationFail({ error: alerts }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}

function* newquotationSR({ id, date, dateFormat, commercialproduct }) {
  const { type: typeProduct, data: dataProduct, alerts: alertsProduct } = yield* getProductInfos(id);
  switch (typeProduct) {
    case 'success':
      const strDate = moment(date).isValid() ? moment(date).format(dateFormat) : moment(new Date()).format(dateFormat);
      const payload = getSalesRouteFromQuotation({
        product: id,
        date: strDate,
        commercial_product: {
          id: commercialproduct.value,
          rec_name: commercialproduct.label,
        },
        broker: get(yield select(getPrefUserInfo), 'dist_network'),
      });

      const { response, error } = yield call(
        fetchFromApi,
        getOptions({ url: API.salesroute.CREATE(), method: 'POST', data: payload })
      );

      const { type, data, alerts } = processResponse({ response, error });
      switch (type) {
        case 'success':
          yield put(fetchNewQuotationSRSuccess({ data: { quotation: data, ...dataProduct } }));
          break;
        case 'fail':
        case 'forbidden':
          yield put(fetchNewQuotationSRFail({ error: alerts }));
          break;
        case 'logout':
          yield put(logout());
          break;
        default:
          break;
      }
      break;
    case 'fail':
    case 'forbidden':
      yield put(fetchNewQuotationSRFail({ error: alertsProduct }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}

function* fetchQuotation({ id }) {
  const { response, error } = yield call(fetchFromApi, getOptions({ url: API.quotation.V1__FETCH__BY_ID(id) }));
  const { type, data, alerts } = processResponse({ response, error });

  switch (type) {
    case 'success':
      yield fetchProduct({ id: data.product });
      yield put(fetchQuotationSuccess(data, id));
      break;
    case 'fail':
    case 'forbidden':
      yield put(fetchQuotationFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}

function* fetchQuotationSR({ id }) {
  const params = getParamForCommercialProductsRequest(new Date(), get(yield select(getSystem), 'dateFormat'));

  const { response: responseCommProd, error: errorCommProd } = yield call(
    fetchFromApi,
    getOptions({ url: API.commercial_product.FETCH__ALL(), params })
  );

  if (responseCommProd) {
    const { response, error } = yield call(fetchFromApi, getOptions({ url: API.salesroute.FETCH__BY_ID(id) }));
    const { type, data, alerts } = processResponse({ response, error });

    switch (type) {
      case 'success':
        if (get(data, 'contracts[0].product.id')) {
          const { type: typeProduct, data: dataProduct, alerts: alertsProduct } = yield* getProductInfos(
            get(data, 'contracts[0].product.id'),
            !!data?.customersData?.duplicatedFrom
          );

          switch (typeProduct) {
            case 'success':
              yield put(
                fetchQuotationSRSuccess(
                  { quotation: data, commercialproducts: responseCommProd.data, ...dataProduct },
                  id
                )
              );
              break;
            case 'fail':
            case 'forbidden':
              yield put(fetchQuotationSRFail({ error: alertsProduct, id }));
              break;
            case 'logout':
              yield put(logout());
              break;
            default:
              break;
          }
        } else {
          yield put(
            fetchQuotationSRFail({ error: ['No product found. Please, retry or contact your administrator'], id })
          );
        }
        break;
      case 'fail':
      case 'forbidden':
        yield put(fetchQuotationSRFail({ error: alerts, id }));
        break;
      case 'logout':
        yield put(logout());
        break;
      default:
        break;
    }
  } else {
    const { type, alerts } = processResponse({ error: errorCommProd });

    switch (type) {
      case 'fail':
      case 'forbidden':
        yield put(fetchQuotationSRFail({ error: alerts }));
        break;
      case 'logout':
        yield put(logout());
        break;
      default:
        break;
    }
  }
}

function* saveSalesroute({ payload, id, next, calc }) {
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({
      url: API.salesroute.PATCH__BY_ID(id),
      method: 'PATCH',
      data: payload,
    })
  );
  if (response) {
    const { data } = response;
    yield put(putQuotationSRSuccess(data));
    let quot;

    switch (next) {
      case 'calculate':
        quot = getQuotationFromSalesRoute(data, get(calc, 'contract'), yield select(getQuotationsStore));
        yield put(fetchCalculateOnlyQuotationSR(quot, 'save'));
        break;
      case 'submit':
        quot = getQuotationFromSalesRoute(data, get(calc, 'contract'), yield select(getQuotationsStore));
        yield put(submitOnlyQuotationsSR(quot, 'save', get(calc, 'contract')));
        break;
      default:
        break;
    }
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      switch (error.response.status) {
        case 401: {
          yield put(logout());
          break;
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          } else {
            yield put(logout());
          }
          break;
        }
        default: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          }
        }
      }
    } finally {
      yield put(putQuotationSRFail(alerts));
    }
  }
  return { response, error };
}

function* savequotation({ payload }) {
  const { id } = payload;
  const dataQuotation = makeCompatibleWithAPIv1({ quotation: payload });
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({
      url: API.quotation.V1__PUT__BY_ID(id),
      method: 'PUT',
      data: dataQuotation,
    })
  );

  if (response) {
    const { data } = response;
    yield put(putQuotationSuccess(data));
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      switch (error.response.status) {
        case 401: {
          yield put(logout());
          break;
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          } else {
            yield put(logout());
          }
          break;
        }
        default: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          }
        }
      }
    } finally {
      yield put(putQuotationFail(alerts));
    }
  }
  return { response, error };
}

function* savequotationSR({ payload, id, next }) {
  const salesR = getSalesRouteFromQuotation(
    makeCompatibleWithAPIv1({ quotation: payload }),
    next !== 'calculate' ? yield select(getQuotationOfferV2, id) : null
  );
  return yield saveSalesroute({ payload: salesR, id, next, calc: payload.calc });
}

function* calculateSR({ payload, next }) {
  const { id } = payload;

  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: API.salesroute.ACTION__SIMULATE_CONTRACTS__BY_ID(id), method: 'POST' })
  );

  if (response) {
    const { data } = response;
    yield put(
      fetchCalculateQuotationSRSuccess(
        {
          quotation: payload,
          salesroute: data,
        },
        id
      )
    );

    const newQuotWithOffer = {
      ...payload,
      offer: getOfferFromV2(get(data, 'contracts'), get(data, 'parties'), get(data, 'loans')),
    };

    switch (next) {
      case 'save':
        yield put(putQuotationSR({ payload: newQuotWithOffer, id }));
        break;
      default:
        break;
    }
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      switch (error.response.status) {
        case 401: {
          yield put(logout());
          break;
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          } else {
            yield put(logout());
          }
          break;
        }
        default: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          }
        }
      }
    } finally {
      yield put(fetchCalculateQuotationSRFail(alerts, id));
    }
  }
}

function* calculateQuotation({ payload }) {
  const { id } = payload;
  const apr = yield* savequotation({ payload });
  if (apr.response) {
    const { response, error } = yield call(
      fetchFromApi,
      getOptions({ url: API.quotation.V1__ACTION__CALCULATE__BY_ID(id), method: 'POST' })
    );

    if (response) {
      const { data } = response;
      yield put(fetchCalculateQuotationSuccess(data, id));
    } else {
      let alerts = ['Unknown error: please, retry or contact your administrator'];
      try {
        switch (error.response.status) {
          case 401: {
            yield put(logout());
            break;
          }
          case 404: {
            alerts = ['404 not found: please, retry or contact your administrator'];
            break;
          }
          case 500: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            } else {
              yield put(logout());
            }
            break;
          }
          default: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            }
          }
        }
      } finally {
        yield put(fetchCalculateQuotationFail(alerts));
      }
    }
  } else {
    yield put(fetchCalculateQuotationFail([]));
  }
}

function* calculateQuotationSR({ payload }) {
  const { id } = payload;
  const storeQuot = yield select(getQuotation, id);
  const newQuot = omit({ ...storeQuot, ...payload }, ['offer']);

  yield put(putQuotationSR({ payload: newQuot, id, next: 'calculate' }));
}

// const saveQuotationUrl =  id => `/api/v1/quotation/${id}/save`; //POST (submit)
// eslint-disable-next-line no-unused-vars
function* submit({ payload }) {
  const { id } = payload;
  const dataQuotation = makeCompatibleWithAPIv1({ quotation: payload });
  const sqr = yield* savequotation({ payload: dataQuotation, id });
  if (sqr.response) {
    // temporary workaround waiting for new portal route
    const { transform } = dataQuotation;
    const { response, error } = yield call(
      fetchFromApi,
      getOptions({
        url: API.quotation.V1__ACTION__SAVE(),
        method: 'POST',
        data: { covered: -1, ...transform },
      })
    );

    if (response) {
      const { data } = response;
      yield put(submitQuotationSuccess(data, id));
    } else {
      // save quotation failed
      let alerts = ['Unknown error: please, retry or contact your administrator'];
      try {
        switch (error.response.status) {
          case 401: {
            yield put(logout());
            break;
          }
          case 404: {
            alerts = ['404 not found: please, retry or contact your administrator'];
            break;
          }
          case 500: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            } else {
              yield put(logout());
            }
            break;
          }
          default: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            }
          }
        }
      } finally {
        yield put(submitQuotationFail(alerts));
      }
    }
  } else {
    yield put(submitQuotationFail([]));
  }
}

function* lenderAddresses({ id }) {
  // const params = {
  //   _fields: 'rec_name',
  //   rec_name: '~*',
  //   party: id,
  // };
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: API.referential.FETCH__LENDER_ADDRESS__BY_ID(id) })
  );

  if (response) {
    const { data } = response;
    yield put(fetchLenderAddressesSuccess(data, id));
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      switch (error.response.status) {
        case 401: {
          yield put(logout());
          break;
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          } else {
            yield put(logout());
          }
          break;
        }
        default: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          }
        }
      }
    } finally {
      yield put(fetchLenderAddressesFail(alerts, id));
    }
  }
}

function* lenders() {
  const params = {
    _fields: 'rec_name',
    rec_name: '~*',
  };
  const extraHeaders = {
    Range: '1-10000',
  };
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: API.referential.FETCH__LENDER(), params, extraHeaders })
  );

  if (response) {
    const { data } = response;
    yield put(fetchLendersSuccess(data));
    // if (Array.isArray(data)) {
    //   yield all(data.map(lender => call(lenderAddresses, { id: lender.id })));
    // }
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      switch (error.response.status) {
        case 401: {
          yield put(logout());
          break;
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          } else {
            yield put(logout());
          }
          break;
        }
        default: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          }
        }
      }
    } finally {
      yield put(fetchLendersFail(alerts));
    }
  }
}

function* kinds() {
  const { response, error } = yield call(fetchFromApi, getOptions({ url: API.loan.V1__FETCH__MODEL_KIND() }));

  if (response) {
    const { data } = response;
    yield put(fetchKindsSuccess(data));
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      switch (error.response.status) {
        case 401: {
          yield put(logout());
          break;
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          } else {
            yield put(logout());
          }
          break;
        }
        default: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          }
        }
      }
    } finally {
      yield put(fetchKindsFail(alerts));
    }
  }
}

function* deferrals() {
  // const { response, error } = yield call(fetchFromApi, getOptions({ url: API.loan.V1__FETCH__MODEL_DEFERRAL() }));
  // NO V2 EQUIVALENT => HARD CODE RESPONSE
  const response = {
    "context": "{}",
    "loading": "lazy",
    "name": "deferral",
    "depends": [
      "first_payment_date",
      "loan_kind",
      "duration_unit",
      "funds_release_date",
      "deferral",
      "rate",
      "deferral_duration",
      "loan_deferral_allowed",
      "state",
      "duration",
      "deferral_balloon_intermediate",
      "increments",
      "payment_frequency"
    ],
    "on_change": [
      "loan_kind",
      "funds_release_date",
      "rate",
      "increments",
      "first_payment_date",
      "duration_unit",
      "deferral",
      "deferral_duration",
      "duration",
      "deferral_balloon_intermediate",
      "payment_frequency",
      "id"
    ],
    "on_change_with": [],
    "readonly": false,
    "required": false,
    "states": "{\"invisible\": {\"__class__\": \"Not\", \"v\": {\"__class__\": \"Bool\", \"v\": {\"__class__\": \"Eval\", \"v\": \"loan_deferral_allowed\", \"d\": \"\"}}}, \"readonly\": {\"__class__\": \"Not\", \"v\": {\"__class__\": \"Equal\", \"s1\": {\"__class__\": \"Eval\", \"v\": \"state\", \"d\": \"\"}, \"s2\": \"draft\"}}}",
    "type": "selection",
    "domain": "[]",
    "searchable": false,
    "sortable": false,
    "string": "Différé",
    "help": "",
    "selection": [
      [
        "",
        "Aucun"
      ],
      [
        "partially",
        "Différé Partiel"
      ],
      [
        "fully",
        "Différé Total"
      ]
    ],
    "selection_change_with": [],
    "sort": true,
    "create": true,
    "delete": true
  }
  if (response) {
    const { data } = response;
    yield put(fetchDeferralsSuccess(data));
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      // switch (error.response.status) {
      //   case 401: {
      //     yield put(logout());
      //     break;
      //   }
      //   case 404: {
      //     alerts = ['404 not found: please, retry or contact your administrator'];
      //     break;
      //   }
      //   case 500: {
      //     if (error.response && error.response.data) {
      //       alerts = getAlerts(error.response);
      //     } else {
      //       yield put(logout());
      //     }
      //     break;
      //   }
      //   default: {
      //     if (error.response && error.response.data) {
      //       alerts = getAlerts(error.response);
      //     }
      //   }
      // }
    } finally {
      yield put(fetchDeferralsFail(alerts));
    }
  }
}

function* banks({ inputValue = undefined }) {
  const params = {
    _fields: 'rec_name',
    // query: 'id,rec_name, bic',
    rec_name: inputValue || '~*',
  };
  const extraHeaders = {
    Range: '1-10000',
  };
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: API.bank.V1__FETCH__ALL(), params, extraHeaders })
  );

  if (response) {
    const { data } = response;
    yield put(fetchBanksSuccess(data));
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      switch (error.response.status) {
        case 401: {
          yield put(logout());
          break;
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          } else {
            yield put(logout());
          }
          break;
        }
        default: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          }
        }
      }
    } finally {
      yield put(fetchBanksFail(alerts));
    }
  }
}

// const printQuotationUrl =  id => `/api/v1/quotation/${id}/print`; //POST (print)
function* print({ payload }) {
  const { id, transform } = payload;
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: API.quotation.V1__ACTION__PRINT__BY_ID(id), method: 'POST', data: transform })
  );

  if (response) {
    const { data } = response;
    yield put(printQuotationSuccess(data, id));
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      switch (error.response.status) {
        case 401: {
          yield put(logout());
          break;
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          } else {
            yield put(logout());
          }
          break;
        }
        default: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          }
        }
      }
    } finally {
      yield put(printQuotationFail(alerts));
    }
  }
}

function* printSR({ payload }) {
  const { id } = payload;
  const storeQuot = yield select(getQuotation, id);
  const quotation = omit({ ...storeQuot, ...omit(payload, ['offer']) }, ['productObj', 'print']);

  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: API.quotation.V2_ACTION_PRINT(), method: 'POST', data: {'salesRoute': quotation} })
  );

  if (response) {
    const { data } = response;
    yield put(printQuotationSRSuccess(data, id));
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      switch (error.response.status) {
        case 401: {
          yield put(logout());
          break;
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          } else {
            yield put(logout());
          }
          break;
        }
        default: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          }
        }
      }
    } finally {
      yield put(printQuotationSRFail(alerts));
    }
  }
}

// const pendQuotationUrl =  id => `/api/v1/quotation/${id}/pend`; //PUT (put on hold)
function* putOnHold({ payload }) {
  const { id } = payload;
  const sqr = yield* savequotation({ payload, id });
  if (sqr.response) {
    const { response, error } = yield call(
      fetchFromApi,
      getOptions({ url: API.quotation.V1__ACTION__PENDING__BY_ID(id), method: 'PUT' })
    );

    if (response) {
      const { data } = response;
      yield put(putOnHoldQuotationSuccess(data, id));
    } else {
      // save quotation failed
      let alerts = ['Unknown error: please, retry or contact your administrator'];
      try {
        switch (error.response.status) {
          case 401: {
            yield put(logout());
            break;
          }
          case 404: {
            alerts = ['404 not found: please, retry or contact your administrator'];
            break;
          }
          case 500: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            } else {
              yield put(logout());
            }
            break;
          }
          default: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            }
          }
        }
      } finally {
        yield put(putOnHoldQuotationFail(alerts));
      }
    }
  } else {
    yield put(putOnHoldQuotationFail([]));
  }
  // const { id } = payload;
  // const { response, error } = yield call(
  //   fetchFromApi,
  //   getOptions({ url: pendQuotationUrl(id), method: 'PUT' })
  // );

  // if (response) {
  //   const { data } = response;
  //   yield put(putOnHoldQuotationSuccess(data));
  // } else {
  //   try {
  //     if (error.response.status === 401 || error.response.status === 500) yield put(logout());
  //   } finally {
  //     yield put(putOnHoldQuotationFail(error));
  //   }
  // }
}

// const cancelQuotationUrl =  id => `/api/v1/quotation/${id}/cancel`; //PUT (cancel)
function* cancel({ payload }) {
  const { id } = payload;
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: API.quotation.V1__ACTION__CANCEL__BY_ID(id), method: 'PUT' })
  );

  if (response) {
    const { data } = response;
    yield put(cancelQuotationSuccess(data, id));
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      switch (error.response.status) {
        case 401: {
          yield put(logout());
          break;
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          } else {
            yield put(logout());
          }
          break;
        }
        default: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          }
        }
      }
    } finally {
      yield put(cancelQuotationFail(alerts, id));
    }
  }
}

function* cancelSR({ payload }) {
  const { id } = payload;
  const salesR = { status: 'CLOSED' };
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: API.salesroute.PATCH__BY_ID(id), method: 'PATCH', data: salesR })
  );

  if (response) {
    const { data } = response;
    yield put(cancelQuotationSRSuccess(data, id));
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      switch (error.response.status) {
        case 401: {
          yield put(logout());
          break;
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          } else {
            yield put(logout());
          }
          break;
        }
        default: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          }
        }
      }
    } finally {
      yield put(cancelQuotationSRFail(alerts, id));
    }
  }
}

function* validateIban({ iban }) {
  const { response, error } = yield call(fetchFromApi, getOptions({ url: API.bank.V1__ACTION__VALIDATE_IBAN(iban) }));

  if (response) {
    const { data } = response;
    yield put(validateIbanSuccess({ data, iban }));
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      switch (error.response.status) {
        case 401: {
          yield put(logout());
          break;
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          } else {
            yield put(logout());
          }
          break;
        }
        default: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          }
        }
      }
    } finally {
      yield put(validateIbanFail({ error: alerts, iban }));
    }
  }
}

function* submitOnlySR({ payload, next, calc }) {
  const { id } = payload;

  const { response, error } = yield call(
    fetchFromApi,
    getOptions({
      url: API.salesroute.ACTION__SUBSCRIBE_CONTRACTS_BY_ID(id),
      method: 'POST',
    })
  );

  if (response) {
    const { data } = response;
    const quot = getQuotationFromSalesRoute(data, calc, yield select(getQuotationsStore));

    yield put(submitQuotationsSRSuccess(quot, id));

    switch (next) {
      case 'save':
        yield put(putQuotationSR({ payload: quot, id }));
        break;
      default:
        break;
    }
  } else {
    // save quotation failed
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      switch (error.response.status) {
        case 401: {
          yield put(logout());
          break;
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          } else {
            yield put(logout());
          }
          break;
        }
        default: {
          if (error.response && error.response.data) {
            alerts = getAlerts(error.response);
          }
        }
      }
    } finally {
      yield put(submitQuotationsSRFail(alerts));
    }
  }
}

function* submitQuotations({ payload }) {
  const { id } = payload;
  const dataQuotation = makeCompatibleWithAPIv1({ quotation: payload });
  const sqr = yield* savequotation({ payload: dataQuotation });
  if (sqr.response) {
    // temporary workaround waiting for new portal route
    const { response, error } = yield call(
      fetchFromApi,
      getOptions({
        url: API.quotation.V1__ACTION__SUBSCRIBE__BY_ID(id),
        method: 'POST',
      })
    );

    if (response) {
      const { data } = response;
      yield put(submitQuotationsSuccess(data, id));
    } else {
      // save quotation failed
      let alerts = ['Unknown error: please, retry or contact your administrator'];
      try {
        switch (error.response.status) {
          case 401: {
            yield put(logout());
            break;
          }
          case 404: {
            alerts = ['404 not found: please, retry or contact your administrator'];
            break;
          }
          case 500: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            } else {
              yield put(logout());
            }
            break;
          }
          default: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            }
          }
        }
      } finally {
        yield put(submitQuotationsFail(alerts));
      }
    }
  } else {
    yield put(submitQuotationsFail([]));
  }
}

function* submitQuotationsSR({ payload }) {
  const { id } = payload;

  const storeQuot = yield select(getQuotation, id);
  const newQuot = { ...storeQuot, ...payload };

  yield put(putQuotationSR({ payload: newQuot, id, next: 'submit' }));
}

function* validateQuotation({ quotation, id }) {
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: API.quotation.V1__ACTION__VALIDATE(), method: 'POST', data: sanitizeQuotation(quotation) })
  );

  if (response) {
    const data = getValidateFront(quotation, response.data, yield select(getQuotationsStrings));
    yield put(validateQuotationSuccess({ data, id }));
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      if (error && error.response) {
        switch (error.response.status) {
          case 401: {
            yield put(logout());
            break;
          }
          case 404: {
            alerts = ['404 not found: please, retry or contact your administrator'];
            break;
          }
          case 500: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            } else {
              yield put(logout());
            }
            break;
          }
          default: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            }
          }
        }
      }
    } finally {
      yield put(validateQuotationFail({ error: alerts, id }));
    }
  }
}

function* validateQuotationSR({ quotation, id }) {
  // const salesRoute = getSalesRouteFromQuotation(sanitizeQuotation(quotation)) TO BE IMPLEMENTED IN CORRECT SOLUTION
  const quot = {
    'salesRoute': quotation
  }
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: API.quotation.V2_ACTION_VALIDATE(), method: 'POST', data: quot })
  );
  if (response) {
    const data = getValidateFront(quotation, response.data, yield select(getQuotationsStrings));
    yield put(validateQuotationSRSuccess({ data, id }));
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      if (error && error.response) {
        switch (error.response.status) {
          case 401: {
            yield put(logout());
            break;
          }
          case 404: {
            alerts = ['404 not found: please, retry or contact your administrator'];
            break;
          }
          case 500: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            } else {
              yield put(logout());
            }
            break;
          }
          default: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            }
          }
        }
      }
    } finally {
      yield put(validateQuotationSRFail({ error: alerts, id }));
    }
  }
}

function* duplicateQuotationSR({ payload, id }) {
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: API.salesroute.ACTION__DUPLICATE_QUOTE(id), method: 'POST', data: payload }) // Legacy
  );

  if (response) {
    yield put(duplicateQuotationSRSuccess({ data: response.data }));
  } else {
    let alerts = ['Unknown error: please, retry or contact your administrator'];
    try {
      if (error && error.response) {
        switch (error.response.status) {
          case 401: {
            yield put(logout());
            break;
          }
          case 404: {
            alerts = ['404 not found: please, retry or contact your administrator'];
            break;
          }
          case 500: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            } else {
              yield put(logout());
            }
            break;
          }
          default: {
            if (error.response && error.response.data) {
              alerts = getAlerts(error.response);
            }
          }
        }
      }
    } finally {
      yield put(duplicateQuotationSRFail({ error: alerts }));
    }
  }
}

function* watchQuotations() {
  yield all([
    yield takeLatest(FETCH_NEW_QUOTATION_REQUEST, newquotation),
    yield takeLatest(FETCH_NEW_QUOTATION_SR_REQUEST, newquotationSR),
    yield takeLatest(FETCH_QUOTATION_REQUEST, fetchQuotation),
    yield takeLatest(FETCH_QUOTATION_SR_REQUEST, fetchQuotationSR),
    yield takeLatest(PUT_QUOTATION_REQUEST, savequotation),
    yield takeLatest(PUT_QUOTATION_SR_REQUEST, savequotationSR),
    yield takeLatest(FETCH_QUOTATIONS_REQUEST, quotations),
    yield takeLatest(FETCH_QUOTATIONS_SR_REQUEST, quotationsSR),
    yield takeLatest(FETCH_PRODUCT_REQUEST, fetchProduct),
    yield takeLatest(FETCH_LENDERS_REQUEST, lenders),
    yield takeLatest(FETCH_LENDER_ADDRESSES_REQUEST, lenderAddresses),
    yield takeLatest(FETCH_KINDS_REQUEST, kinds),
    yield takeLatest(FETCH_DEFERRALS_REQUEST, deferrals),
    yield takeLatest(FETCH_CALCULATE_REQUEST, calculateQuotation),
    yield takeLatest(FETCH_CALCULATE_SR_REQUEST, calculateQuotationSR),
    yield takeLatest(FETCH_CALCULATE_ONLY_SR_REQUEST, calculateSR),
    yield takeLatest(SUBMIT_QUOTATION_REQUEST, submit),
    yield takeLatest(CANCEL_QUOTATION_REQUEST, cancel),
    yield takeLatest(CANCEL_QUOTATION_SR_REQUEST, cancelSR),
    yield takeLatest(PUTONHOLD_QUOTATION_REQUEST, putOnHold),
    yield takeLatest(PRINT_QUOTATION_REQUEST, print),
    yield takeLatest(PRINT_QUOTATION_SR_REQUEST, printSR),
    yield takeLatest(FETCH_BANKS_REQUEST, banks),
    yield takeLatest(VALIDATE_IBAN_REQUEST, validateIban),
    yield takeLatest(DUPLICATE_QUOTATION_SR_REQUEST, duplicateQuotationSR),
    yield takeLatest(SUBMIT_QUOTATIONS_REQUEST, submitQuotations),
    yield takeLatest(SUBMIT_QUOTATIONS_SR_REQUEST, submitQuotationsSR),
    yield takeLatest(SUBMIT_ONLY_QUOTATIONS_SR_REQUEST, submitOnlySR),
    yield takeLeading(VALIDATE_QUOTATION_REQUEST, validateQuotation),
    yield takeLeading(VALIDATE_QUOTATION_SR_REQUEST, validateQuotationSR),
  ]);
}

export default watchQuotations;
